import {
  curatedCoworldName,
  curatedLeagueName,
} from "../../coworlds/coworld-curation";
import type { GameDestinationContext } from "@softmax/web-addresses";

export type PortfolioSeatLike = {
  readonly league_id: string;
  readonly game_name: string;
};
export type CoworldTarget = {
  readonly name: string;
  readonly corpusSlug: string;
  readonly route: GameDestinationContext;
};
type CoworldOption = CoworldTarget & {
  readonly member: boolean;
};
export type DirectoryEntryLike = CoworldTarget & {
  readonly league_id: string;
  readonly default_league_id: string | null;
  readonly coworld: string | null;
};

export function buildDirectoryCoworlds(
  directory: DirectoryEntryLike[],
): CoworldTarget[] {
  const coworlds = new Map<string, CoworldTarget>();

  for (const entry of directory) {
    if (
      coworlds.has(entry.route.game.id) &&
      entry.league_id !== entry.default_league_id
    )
      continue;

    coworlds.set(entry.route.game.id, {
      name: curatedCoworldName(entry.corpusSlug) ?? entry.name,
      corpusSlug: entry.corpusSlug,
      route: { game: entry.route.game },
    });
  }

  return [...coworlds.values()];
}

export function leaguesOfCoworld({
  directory,
  gameSlug,
}: {
  readonly directory: DirectoryEntryLike[];
  readonly gameSlug: string;
}): DirectoryEntryLike[] {
  const byLeague = new Map<string, DirectoryEntryLike>();

  for (const entry of directory) {
    if (entry.route.game.slug !== gameSlug) continue;

    if (!byLeague.has(entry.league_id))
      byLeague.set(entry.league_id, {
        ...entry,
        name: curatedLeagueName({ name: entry.name }),
      });
  }

  if (byLeague.size < 2) return [];

  return [...byLeague.values()].toSorted((a, b) =>
    a.name.localeCompare(b.name),
  );
}

export function matchDirectoryCoworlds({
  directory,
  query,
  alreadyShown,
}: {
  readonly directory: DirectoryEntryLike[];
  readonly query: string;
  readonly alreadyShown: ReadonlySet<string>;
}): CoworldOption[] {
  const trimmed = query.trim().toLowerCase();

  if (!trimmed) return [];

  return buildDirectoryCoworlds(
    directory.filter((entry) => entry.route.game.catalog_status === "listed"),
  )
    .filter(
      (coworld) =>
        coworld.name.toLowerCase().includes(trimmed) &&
        !alreadyShown.has(coworld.route.game.id),
    )
    .toSorted((a, b) => a.name.localeCompare(b.name))
    .map((coworld) => ({ ...coworld, member: false }));
}

export function buildCoworldOptions({
  seats,
  targetOf,
  current,
}: {
  readonly seats: PortfolioSeatLike[];
  readonly targetOf: (leagueId: string) => CoworldTarget | null;
  readonly current: CoworldTarget | null;
}): CoworldOption[] {
  const seen = new Set<string>();
  const memberships: CoworldOption[] = [];

  for (const seat of seats) {
    const target = targetOf(seat.league_id);

    if (!target || seen.has(target.route.game.id)) continue;

    seen.add(target.route.game.id);
    memberships.push({
      name: curatedCoworldName(target.corpusSlug) ?? seat.game_name,
      corpusSlug: target.corpusSlug,
      route: { game: target.route.game },
      member: true,
    });
  }

  const ordered = memberships.toSorted((a, b) => a.name.localeCompare(b.name));

  if (!current) return ordered;

  const currentIndex = ordered.findIndex(
    (membership) => membership.route.game.id === current.route.game.id,
  );

  if (currentIndex === -1) return [{ ...current, member: false }, ...ordered];

  const currentOptions = ordered.splice(currentIndex, 1);
  return [...currentOptions, ...ordered];
}
